/**
 * 認証必須ルート
 * #2133 設計 §3: 権限の出し分けを行う。判定できないとき（role===null）は
 * 見せない（fail-closed）。
 *
 * チェック順序:
 * 1. loading/claimsLoading 中はスピナー
 * 2. 未認証なら /login へ
 * 3. 初回パスワード変更が未完了なら /password-change へ
 * 4. role が null（取得失敗・権限なし）、または instructor・student なら
 *    /unavailable へ（このWebは admin・companyAssociate・studioAssociate のみ）
 * 5. それ以外は children を表示
 */
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Spinner } from '@/components/ui/Spinner';
import type { ReactNode } from 'react';

interface ProtectedRouteProps {
  children: ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, loading, claimsLoading, needsPasswordChange, roleFlags } = useAuth();
  const location = useLocation();

  if (loading || claimsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (needsPasswordChange) {
    return <Navigate to="/password-change" replace />;
  }

  if (!roleFlags.canUseWeb) {
    return <Navigate to="/unavailable" replace />;
  }

  return <>{children}</>;
}
